/**
 * The device-side print path: every job is checked against its model's guard
 * right before the bytes leave the device, whatever the transport.
 */
import { toMonoBitmap, type RgbaImage } from "./bitmap.ts";
import type { Media, PrinterModel } from "./registry.ts";

export type TransportKind = "ble" | "serial" | "usb" | "tcp";

export interface Transport {
  readonly name: string;
  readonly kind: TransportKind;
  write(bytes: Uint8Array): Promise<void>;
  close(): Promise<void>;
}

export interface Connection {
  model: PrinterModel;
  transport: Transport;
}

export class UnsafeJobError extends Error {
  constructor(readonly reason: string) {
    super(`Refused to send the label: ${reason}.`);
    this.name = "UnsafeJobError";
  }
}

/** Check a finished job against the model and send it. Nothing is written if the check fails. */
export async function sendJob(conn: Connection, job: Uint8Array): Promise<void> {
  const problem = conn.model.check(job);
  if (problem) throw new UnsafeJobError(problem);
  await conn.transport.write(job);
}

/** Build the job for a drawn label. */
export async function buildJob(model: PrinterModel, media: Media, image: RgbaImage, copies = 1): Promise<Uint8Array<ArrayBuffer>> {
  if (image.width !== media.canvasWidthDots || image.height !== media.heightDots) {
    throw new Error(
      `label image is ${image.width}×${image.height} dots; ${media.label} on the ${model.name} needs ${media.canvasWidthDots}×${media.heightDots}`
    );
  }
  return model.encode(toMonoBitmap(image), media, copies);
}

export async function printLabel(conn: Connection, media: Media, image: RgbaImage, copies = 1): Promise<void> {
  await sendJob(conn, await buildJob(conn.model, media, image, copies));
}

export interface ChunkPlan {
  chunkBytes: number;
  pauseEveryBytes: number;
  pauseMs: number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Write `data` in chunks, pausing after every `pauseEveryBytes` so a small printer buffer keeps up. */
export async function writeChunked(
  data: Uint8Array,
  plan: ChunkPlan,
  write: (part: Uint8Array<ArrayBuffer>) => Promise<void>,
  pause: (ms: number) => Promise<void> = sleep
): Promise<void> {
  if (plan.chunkBytes < 1) throw new Error("chunk size must be at least 1 byte");
  let sinceLastPause = 0;
  for (let i = 0; i < data.length; i += plan.chunkBytes) {
    const part = data.slice(i, i + plan.chunkBytes);
    await write(part);
    sinceLastPause += part.length;
    if (sinceLastPause >= plan.pauseEveryBytes && i + plan.chunkBytes < data.length) {
      await pause(plan.pauseMs);
      sinceLastPause = 0;
    }
  }
}
